<template>
  <article
    class="highlight-card"
    :class="{ 'highlight-card--fact': !(image || $slots.media) }"
  >
    <div class="highlight-card__media">
      <slot name="media">
        <img
          class="highlight-card__image"
          loading="lazy"
          v-if="image"
          :alt="image.alt"
          :class="{ 'highlight-card__image--cover': image.cover }"
          :src="image.src"
        >
        <div class="highlight-card__fact" v-else-if="fact">
          <svg
            aria-hidden="true"
            class="highlight-card__fact-icon"
            focusable="false"
            :viewBox="FACT_ICONS[fact.icon]"
          >
            <use :href="`#${fact.icon}`" />
          </svg>
          <p class="highlight-card__fact-text" v-text="fact.text" />
          <p
            class="highlight-card__fact-detail"
            v-if="fact.detail"
            v-text="fact.detail"
          />
        </div>
      </slot>
    </div>

    <div class="highlight-card__body">
      <h3 class="highlight-card__heading">{{ heading }}</h3>
      <p class="highlight-card__text" v-if="text" v-text="text" />
      <p class="highlight-card__status" v-if="status" v-text="status" />
      <div
        class="highlight-card__actions"
        :class="{ 'highlight-card__actions--split': secondaryButton }"
      >
        <p
          class="highlight-card__actions-label"
          v-if="secondaryButton && buttonsLabel"
          v-text="buttonsLabel"
        />
        <template v-for="(item, i) in buttons" :key="i">
          <a
            class="btn obituary highlight-card__button"
            v-if="item.href"
            :href="item.href"
            :rel="item.external ? 'noopener' : undefined"
            :target="item.external ? '_blank' : undefined"
          >
            {{ item.label }}
          </a>
          <button
            class="btn obituary highlight-card__button"
            type="button"
            v-else
            @click="emit('action', i)"
          >
            {{ item.label }}
          </button>
        </template>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
  /** The site's line illustrations in the obituary sprite, with their viewBox. */
  export const FACT_ICONS = {
    customCandle: "0 0 24 24",
    customFlower: "0 0 23 35",
    customGift: "0 0 46.5 29.3",
    customMedia: "0 0 70 59",
  } as const;

  export type FactIcon = keyof typeof FACT_ICONS;

  export interface CardFact {
    detail?: string;
    icon: FactIcon;
    text: string;
  }

  export interface CardImage {
    alt: string;
    /** Fill the area, for photos; product shots are shown whole. */
    cover?: boolean;
    src: string;
  }

  /** The one quiet outline button: a link with `href`, otherwise it emits `action`. */
  export interface CardButton {
    external?: boolean;
    href?: string;
    label: string;
  }
</script>

<script lang="ts" setup>
  import { computed } from "vue";

  /**
   * One highlight: media (a real image, the `media` slot, or else a fact box with the
   * site's line illustration), heading, text, status line and one button (or two).
   */
  const props = defineProps<{
    button: CardButton;
    /** Label above the buttons, shown only with a `secondaryButton`. */
    buttonsLabel?: string;
    fact?: CardFact | null;
    heading: string;
    image?: CardImage | null;
    /** A second button beside `button`; an `href` makes it a link, as for `button`. */
    secondaryButton?: CardButton;
    status?: string;
    text?: string;
  }>();

  const buttons = computed(() =>
    props.secondaryButton
      ? [props.button, props.secondaryButton]
      : [props.button]
  );

  /** Which button: 0 is `button`, 1 the `secondaryButton`. */
  const emit = defineEmits<{ action: [index: number] }>();
</script>
